import { MicDeviceMenuItem } from '@modules/input-output-devices/MicDeviceMenuItem';
import {
  Button,
  ButtonProps,
  Divider,
  IconButton,
  Menu,
  Popover,
  Stack,
  Tooltip,
  Typography,
  keyframes,
  styled,
} from '@mui/material';
import cn from 'classnames';
import DetectRTC from 'detectrtc';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { ReactNode, forwardRef, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useLocales } from 'src/locales';
import { useRecorderClips } from 'src/state/recorderHistory/useRecorderClips';
import { useRecorderState } from 'src/state/recorderState';
import type { RecorderFileReadyOutput } from 'src/utils/recorder';
import { pxToRem } from '../../theme/typography';
import { CircularProgressWithLabel } from '../CircularProgressWithLabel';
import { Timer } from '../Timer';
import Iconify from '../iconify/Iconify';
import RecordListItem from './RecordListItem';
import { useScreenRecorderPreviewModal } from './index';

import { blobToBase64 } from './utils';

type ValuesByStatusType = {
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  tooltip: string;
  render: ReactNode;
};

type Props = ButtonProps & {
  uploadRecordInstantly?: (record: Blob | null) => void;
  disabled?: boolean;
  children?: React.ReactNode;
};

type MicDevice = {
  deviceId: string;
  id: string;
  label: string;
};

export const ScreenRecord = forwardRef<any, Props>(
  ({ uploadRecordInstantly, disabled, children, ...rest }, ref) => {
    const [progress, setProgress] = useState(0);
    const [micDevice, setMicDevice] = useState<MicDevice>();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const { showScreenRecorderPreview } = useScreenRecorderPreviewModal();
    const { status, timer, stopRecording, startRecording, setStatus } =
      useRecorderState();

    const { user } = useAuthContext();
    const { translate } = useLocales();
    const navigate = useNavigate();

    const optionState = usePopupState({
      variant: 'popover',
      popupId: 'demoPopover',
    });

    const { videos, addVideo, removeVideo } = useRecorderClips(
      user?._id,
      user?.workspace._id,
    );
    const handleStopRecord = async () => {
      stopRecording();
    };

    const handleSelectMic = (deviceId: string) => {
      const device = DetectRTC.audioInputDevices.find(
        (input) => input.deviceId === deviceId || input.id === deviceId,
      );
      if (device) {
        setMicDevice(device);
      }
    };

    const handleStartRecord = async () => {
      await startRecording({
        onFileReady: async ({
          mediaBlob,
          mediaBlobUrl,
        }: RecorderFileReadyOutput) => {
          const video = {
            mediaBlob: await blobToBase64(mediaBlob),
            mediaBlobUrl,
            createdAt: new Date().toISOString(),
          };
          addVideo(video);

          if (uploadRecordInstantly) {
            setStatus('idle');
            return uploadRecordInstantly(mediaBlob);
          }

          showScreenRecorderPreview({ mediaBlobUrl, mediaBlob });
          setStatus('idle');
        },
        onProgress(currentProgress) {
          setProgress(currentProgress);
        },
        micDeviceId: micDevice?.deviceId,
      });
      setAnchorEl(null);
    };

    const handleOpenPopover = async (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
      setAnchorEl(null);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const valuesByStatus: ValuesByStatusType | undefined = useMemo(() => {
      return {
        idle: {
          onClick: handleOpenPopover,
          tooltip: translate('screen_record.title'),
          render: <Iconify icon="tabler:video" />,
        },
        recording: {
          onClick: handleStopRecord,
          tooltip: translate('screen_record.stop_recording'),
          render: (
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
            >
              <IconScreenRecording icon="tabler:player-record-filled" />
              <TimeScreenRecording variant="caption">
                {!!timer && <Timer startDate={timer} play />}
              </TimeScreenRecording>
            </Stack>
          ),
        },
        loading: {
          onClick: () => {},
          tooltip: translate('shared.loading.loading_video'),
          render: <CircularProgressWithLabel value={progress} />,
        },
      }[status.toString()];
    }, [status, progress, translate, timer]);

    useEffect(() => {
      DetectRTC.load(() => {
        setMicDevice(DetectRTC.audioInputDevices[0]);
      });
    }, []);

    if (!valuesByStatus) return null;

    return (
      <>
        <Tooltip placement="bottom" arrow title={valuesByStatus.tooltip}>
          <StyledIconButton
            ref={ref}
            onClick={valuesByStatus.onClick}
            color={status === 'recording' ? 'error' : 'inherit'}
            className={cn({ selected: !!anchorEl })}
            disabled={disabled}
            {...rest}
          >
            {valuesByStatus.render}
          </StyledIconButton>
        </Tooltip>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Stack p={2} gap={2} width="270px">
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="inherit">
                {translate('screen_record.title')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                endIcon={<Iconify icon="tabler:video" />}
                onClick={() =>
                  navigate(
                    PATH_DASHBOARD.drive.folder(
                      user?.drive?.recordingFolderId || '',
                    ),
                  )
                }
              >
                <Typography variant="caption">
                  {translate('screen_record.go_to_clips')}
                </Typography>
              </Button>
            </Stack>
            <Stack>
              <Stack gap={1}>
                {!!micDevice && (
                  <MicDeviceMenuItem
                    {...bindTrigger(optionState)}
                    key="mic-selection-menu"
                    icon="tabler:microphone"
                    device={micDevice}
                    selected={true}
                  />
                )}

                <Menu
                  id="mic-selection-menu"
                  {...bindPopover(optionState)}
                  onClose={optionState.close}
                >
                  {DetectRTC.audioInputDevices.map((input: MicDevice) => (
                    <MicDeviceMenuItem
                      key={input?.deviceId}
                      device={input}
                      icon="tabler:check"
                      selected={input?.deviceId === micDevice?.deviceId}
                      onClick={() => {
                        handleSelectMic(input.deviceId);
                        optionState.close();
                      }}
                    />
                  ))}
                </Menu>
              </Stack>
            </Stack>
            {children}
            <Button
              variant="contained"
              onClick={handleStartRecord}
              disabled={disabled}
            >
              {translate('screen_record.start_record')}
            </Button>
            {!!videos.length && <Divider />}
            <Stack>
              {videos.map((video, i) => (
                <RecordListItem
                  key={`recorder-#${i}`}
                  title={translate('screen_record.clips_count', {
                    index: i + 1,
                  })}
                  date={video.createdAt}
                  openPreview={() =>
                    showScreenRecorderPreview({
                      mediaBlobUrl: video.mediaBlobUrl,
                      mediaBlob: video.mediaBlob,
                    })
                  }
                  removeRecord={() => removeVideo(video.createdAt)}
                />
              ))}
            </Stack>
          </Stack>
        </Popover>
      </>
    );
  },
);

const ringPulse = keyframes`
  0% {
    transform: scale(0.5, 0.5);
    opacity: 0.1;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: scale(1.3, 1.3);
    opacity: 0.1;
  }
`;

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: theme.spacing(1),
  '&.selected': {
    backgroundColor: theme.palette.action.selected,
  },
}));

export const IconScreenRecording = styled(Iconify)(({ theme }) => ({
  animation: `${ringPulse} 1s ease-out`,
  animationIterationCount: 'infinite',
  width: '10px',
  height: '10px',
}));

const TimeScreenRecording = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(10),
}));
